/**
 * A page as a stack of blocks, edited in the Appearance preview.
 *
 * Text, masonry and directory pages are one kind of page: a vertical stack.
 * Slideshow and fullscreen own the viewport, so they stay page types.
 *
 * The stack only exists in the editor's draft. Saving folds it back into the
 * legacy content and gallery fields, and stores `blocks` only when the stack
 * holds more than one text and one gallery, so a simple page reads exactly as
 * it did for visitors.
 */

import {
    BLOCK_TYPES,
    GALLERY_LAYOUTS,
    ITEM_TYPES,
    blocksFromLegacy,
    createBlock,
    legacyFromBlocks,
    needsBlockStorage,
    slideshowDraft,
} from './schema.js';
import { hasActiveFilter } from './filter.js';

export const STACK_GALLERY_TYPES = ['none', 'html', GALLERY_LAYOUTS.MASONRY, GALLERY_LAYOUTS.DIRECTORY];

export function isStackPage(draft) {
    return STACK_GALLERY_TYPES.includes(draft?.gallery?.type || 'none');
}

/** The Layout picker value: 'page' for the stack, else the gallery type. */
export function pageLayoutOf(draft) {
    return isStackPage(draft) ? 'page' : (draft?.gallery?.type || 'none');
}

/** Compile a text, masonry or directory draft into blocks. */
export function stackDraft(draft) {
    if (!draft || !isStackPage(draft)) return draft;
    // An explicit list, including [], is the editor's stack. Only synthesize
    // when the page has never been opened as blocks (blocks missing).
    if (Array.isArray(draft.blocks)) return draft;

    const gallery = draft.gallery || { type: 'none', images: [] };
    const type = gallery.type === 'html' ? 'none' : (gallery.type || 'none');
    let blocks = blocksFromLegacy({ ...draft, blocks: undefined, gallery: { ...gallery, type } });
    // A masonry never showed its stale directory links, so the stack does not either.
    blocks = blocks.map((block) => (
        block.type === BLOCK_TYPES.GALLERY && block.layout !== GALLERY_LAYOUTS.DIRECTORY
            ? { ...block, links: [] }
            : block
    ));
    if (blocks.length === 0) {
        blocks = [{
            id: 'legacy-richtext',
            type: BLOCK_TYPES.RICHTEXT,
            html: draft.content || '',
            maxWidth: draft.contentMaxWidth ?? 65,
        }];
    }
    return { ...draft, blocks };
}

/**
 * Switch the page Layout. Leaving the stack folds its first text and gallery
 * into the legacy fields that slideshow and fullscreen read.
 */
export function setPageLayout(draft, layout) {
    if (!draft) return draft;
    const gallery = draft.gallery || { type: 'none', images: [] };
    if (layout === 'page') {
        if (isStackPage(draft)) return draft;
        const type = (gallery.images || []).length ? GALLERY_LAYOUTS.MASONRY : 'none';
        const next = { ...draft, gallery: { ...gallery, type } };
        delete next.blocks;
        delete next.slideshow;
        return stackDraft(next);
    }
    if (!isStackPage(draft)) {
        const next = { ...draft, gallery: { ...gallery, type: layout } };
        if (layout === GALLERY_LAYOUTS.SLIDESHOW) return slideshowDraft(next);
        if (draft.gallery?.type === GALLERY_LAYOUTS.SLIDESHOW) delete next.slideshow;
        return next;
    }
    const legacy = legacyFromBlocks(draft.blocks || []);
    // Fullscreen / slideshow keep text + vectors as overlay blocks (not a stack).
    const overlay = (draft.blocks || []).filter(
        (block) => block?.type === BLOCK_TYPES.RICHTEXT || block?.type === BLOCK_TYPES.VECTOR,
    );
    const next = {
        ...draft,
        content: legacy.content,
        contentMaxWidth: legacy.contentMaxWidth,
        gallery: {
            ...gallery,
            ...(legacy.gallery ? { images: legacy.gallery.images } : {}),
            type: layout,
        },
    };
    if (layout === GALLERY_LAYOUTS.SLIDESHOW) {
        // Its text is these blocks from now on; with none, an empty Text to type into.
        delete next.blocks;
        return slideshowDraft(overlay.length ? { ...next, blocks: overlay, slideshow: {} } : next);
    }
    if (overlay.length) next.blocks = overlay;
    else delete next.blocks;
    return next;
}

const isMargin = (key) => key === 'marginTop' || key === 'marginBottom';

function marginIsZero(value) {
    if (value && typeof value === 'object') return !(Number(value.value) || 0);
    return !(Number(value) || 0);
}

/** A value with ids, defaults and empty fields removed, for comparing two stacks. */
function comparable(value, { blockLevel = false } = {}) {
    if (Array.isArray(value)) {
        const list = value.map((entry) => comparable(entry, { blockLevel })).filter((entry) => entry !== undefined);
        return list.length ? list : undefined;
    }
    if (value && typeof value === 'object') {
        const source = blockLevel && value.type === BLOCK_TYPES.RICHTEXT
            ? { maxWidth: 65, ...value }
            : blockLevel && value.type === BLOCK_TYPES.GALLERY
                ? { layout: GALLERY_LAYOUTS.MASONRY, ...value }
                : value;
        const out = {};
        Object.keys(source).sort().forEach((key) => {
            if (blockLevel && key === 'id') return;
            if (isMargin(key) && marginIsZero(source[key])) return;
            if (key === 'filter' && !hasActiveFilter(source[key])) return;
            const next = comparable(source[key], { blockLevel: key === 'cells' });
            if (next !== undefined) out[key] = next;
        });
        return Object.keys(out).length ? out : undefined;
    }
    if (value === undefined || value === null || value === false || value === '') return undefined;
    return value;
}

/**
 * True when saving these blocks as legacy fields alone and reading them back
 * (what the next load does) gives the same stack. Anything else, such as a
 * setting the legacy fields have no place for, must be stored as blocks or it
 * reverts on save.
 */
export function stackFoldsLosslessly(blocks, legacyFields) {
    const rebuilt = stackDraft({
        content: legacyFields.content,
        contentMaxWidth: legacyFields.contentMaxWidth,
        gallery: legacyFields.gallery,
    })?.blocks || [];
    return JSON.stringify(comparable(rebuilt, { blockLevel: true }) ?? null)
        === JSON.stringify(comparable(blocks, { blockLevel: true }) ?? null);
}

/**
 * Legacy fields and `blocks` for PUT. `blocks: []` tells the API the page no
 * longer needs block storage.
 */
export function stackBodyFields(draft) {
    const blocks = Array.isArray(draft?.blocks) ? draft.blocks : [];
    const legacy = legacyFromBlocks(blocks);
    const gallery = draft?.gallery || { type: 'none', images: [] };
    let nextGallery;
    if (legacy.gallery) {
        nextGallery = {
            ...gallery,
            ...legacy.gallery,
            links: legacy.gallery.type === GALLERY_LAYOUTS.DIRECTORY ? legacy.gallery.links : (gallery.links || []),
        };
        if (!legacy.gallery.crops) delete nextGallery.crops;
    } else {
        nextGallery = { ...gallery, type: 'none' };
    }
    const fields = {
        content: legacy.content,
        contentMaxWidth: legacy.contentMaxWidth,
        gallery: nextGallery,
    };
    const store = needsBlockStorage(blocks) || !stackFoldsLosslessly(blocks, fields);
    return { ...fields, blocks: store ? blocks : [] };
}

function mapBlocks(blocks, fn) {
    return (blocks || []).map((block) => {
        const next = fn(block);
        if (next.type === BLOCK_TYPES.COLUMNS && Array.isArray(next.cells)) {
            return { ...next, cells: mapBlocks(next.cells, fn) };
        }
        return next;
    });
}

export function findBlock(blocks, id) {
    for (const block of blocks || []) {
        if (block.id === id) return block;
        if (block.type === BLOCK_TYPES.COLUMNS) {
            const inner = findBlock(block.cells, id);
            if (inner) return inner;
        }
    }
    return null;
}

export function updateBlock(blocks, id, patch) {
    return mapBlocks(blocks, (block) => (block.id === id ? { ...block, ...patch } : block));
}

export function removeBlock(blocks, id) {
    return (blocks || []).filter((block) => block.id !== id);
}

export function moveBlock(blocks, id, delta) {
    const list = [...(blocks || [])];
    const index = list.findIndex((block) => block.id === id);
    const target = index + delta;
    if (index === -1 || target < 0 || target >= list.length) return list;
    [list[index], list[target]] = [list[target], list[index]];
    return list;
}

/** Move `id` to just before or just after `targetId`. Returns the same array when nothing moves. */
export function reorderBlocks(blocks, id, targetId, position = 'before') {
    const source = blocks || [];
    const list = [...source];
    const from = list.findIndex((block) => block.id === id);
    if (from < 0 || !targetId || id === targetId) return source;
    const [item] = list.splice(from, 1);
    let to = list.findIndex((block) => block.id === targetId);
    if (to < 0) return source;
    if (position === 'after') to += 1;
    list.splice(to, 0, item);
    const same = list.every((block, index) => block === source[index]);
    return same ? source : list;
}

/** Insert a new block after `afterId`, or at the end. Returns { blocks, block }. */
export function insertBlock(blocks, type, afterId, overrides = {}) {
    const block = type === BLOCK_TYPES.GALLERY
        ? createBlock(type, { links: [], ...overrides })
        : createBlock(type, overrides);
    const list = [...(blocks || [])];
    const index = afterId ? list.findIndex((entry) => entry.id === afterId) : -1;
    list.splice(index === -1 ? list.length : index + 1, 0, block);
    return { blocks: list, block };
}

/** Key a gallery tile by what the preview puts in its data-page-id. */
export function itemKey(item) {
    if (!item) return '';
    if (item.type === ITEM_TYPES.IMAGE) return item.fileId;
    if (item.type === ITEM_TYPES.PAGE) return item.pageId;
    if (item.type === ITEM_TYPES.SPREAD) return (item.sides || []).map((side) => side.fileId).join('+');
    return item.videoId || item.fileId || '';
}

/**
 * Put the entries named in `ids` into that order, inside the slots they
 * already occupy. Entries that are not tiles in the preview keep their place.
 */
export function reorderSubset(list, keyOf, ids) {
    const wanted = new Set(ids);
    const queue = ids
        .map((id) => (list || []).find((entry) => keyOf(entry) === id))
        .filter(Boolean);
    let next = 0;
    return (list || []).map((entry) => (wanted.has(keyOf(entry)) && next < queue.length ? queue[next++] : entry));
}

/**
 * Apply a tile edit sent from a grid in the preview.
 * @param {Object} block - A gallery block
 * @param {Object} edit - { action: 'order'|'remove'|'update-link'|..., kind: 'image'|'link', ids?, id?, patch? }
 */
export function editGalleryBlock(block, edit) {
    if (!block || block.type !== BLOCK_TYPES.GALLERY || !edit) return block;
    const items = block.items || [];
    const links = block.links || [];
    if (edit.action === 'order' && Array.isArray(edit.ids)) {
        if (edit.kind === 'link') {
            // Tiles added from the block editor live on items, while older
            // directory pages live on links. The preview shows links first and
            // then those items, so a reorder has to write one list or the last
            // added page stays glued to the bottom.
            const fromItems = items
                .filter((item) => item.type === ITEM_TYPES.PAGE && item.pageId)
                .map((item) => ({
                    pageId: item.pageId,
                    description: item.description || '',
                    imageIds: item.imageIds || [],
                    ...(item.cropRatio !== undefined ? { cropRatio: item.cropRatio } : {}),
                    ...(item.cycleDuration !== undefined ? { cycleDuration: item.cycleDuration } : {}),
                    crops: item.crops || {},
                }));
            const combined = [...links, ...fromItems];
            return {
                ...block,
                links: reorderSubset(combined, (link) => link.pageId, edit.ids),
                items: items.filter((item) => item.type !== ITEM_TYPES.PAGE),
            };
        }
        return { ...block, items: reorderSubset(items, (item) => (item.type === ITEM_TYPES.IMAGE ? item.fileId : null), edit.ids) };
    }
    if (edit.action === 'remove' && edit.id) {
        if (edit.kind === 'link') {
            return {
                ...block,
                links: links.filter((link) => link.pageId !== edit.id),
                items: items.filter((item) => !(item.type === ITEM_TYPES.PAGE && item.pageId === edit.id)),
            };
        }
        const nextCrops = { ...(block.crops || {}) };
        delete nextCrops[edit.id];
        return {
            ...block,
            items: items.filter((item) => itemKey(item) !== edit.id),
            crops: nextCrops,
        };
    }
    if (edit.action === 'add-images' && Array.isArray(edit.fileIds) && edit.fileIds.length) {
        return insertGalleryImages(block, edit.fileIds, edit.beforeId);
    }
    if (edit.action === 'crop' && edit.id && edit.pageId) {
        const apply = (link) => {
            if (!link || link.pageId !== edit.pageId) return link;
            const crops = { ...(link.crops || {}) };
            if (edit.crop) crops[edit.id] = edit.crop;
            else delete crops[edit.id];
            return { ...link, crops };
        };
        return {
            ...block,
            links: links.map(apply),
            items: items.map((item) => (
                item.type === ITEM_TYPES.PAGE ? apply(item) : item
            )),
        };
    }
    if (edit.action === 'update-link' && edit.id && edit.patch && typeof edit.patch === 'object') {
        const allowed = ['description', 'imageIds', 'cycleDuration', 'crops'];
        const patch = Object.fromEntries(Object.entries(edit.patch).filter(([key]) => allowed.includes(key)));
        if (!Object.keys(patch).length) return block;
        let changed = false;
        const apply = (link) => {
            if (!link || link.pageId !== edit.id) return link;
            changed = true;
            const next = { ...link, ...patch };
            if (patch.imageIds && link.crops) {
                const keep = new Set(patch.imageIds);
                next.crops = Object.fromEntries(Object.entries(next.crops || {}).filter(([id]) => keep.has(id)));
            }
            return next;
        };
        const nextLinks = links.map(apply);
        const nextItems = items.map((item) => (item.type === ITEM_TYPES.PAGE ? apply(item) : item));
        return changed ? { ...block, links: nextLinks, items: nextItems } : block;
    }
    if (edit.action === 'crop' && edit.id) {
        const nextCrops = { ...(block.crops || {}) };
        if (edit.crop) nextCrops[edit.id] = edit.crop;
        else delete nextCrops[edit.id];
        return { ...block, crops: nextCrops };
    }
    return block;
}

/** A gallery block without the tiles keyed by `ids`, and without their crops. */
export function removeGalleryItems(block, ids) {
    if (!block || block.type !== BLOCK_TYPES.GALLERY) return block;
    const drop = new Set(ids || []);
    const items = block.items || [];
    const kept = items.filter((item) => !drop.has(itemKey(item)));
    if (kept.length === items.length) return block;
    const crops = { ...(block.crops || {}) };
    drop.forEach((id) => { delete crops[id]; });
    return { ...block, items: kept, crops };
}

function legacyEntry(item) {
    if (item.type === ITEM_TYPES.SPREAD) return (item.sides || []).map((side) => side.fileId);
    const { type, fileId, ...rest } = item;
    return Object.keys(rest).length ? { id: fileId, ...rest } : fileId;
}

/**
 * Move tiles out of a gallery block onto another page's draft: its first
 * masonry, or a new masonry at the end. A page that is not built from blocks
 * (slideshow, fullscreen) takes them on its own gallery. Crops go with them.
 * @param {Object} block - the source gallery block
 * @param {string[]} ids - tile keys, as the preview sends them
 * @param {Object} target - the other page's draft
 * @returns {{ block: Object, target: Object, moved: number }}
 */
export function moveGalleryItems(block, ids, target) {
    const wanted = new Set(ids || []);
    const moving = (block?.items || []).filter((item) => wanted.has(itemKey(item)));
    if (!moving.length || !target) return { block, target, moved: 0 };
    const crops = {};
    moving.forEach((item) => {
        const key = itemKey(item);
        if (block.crops?.[key]) crops[key] = block.crops[key];
    });
    const next = { block: removeGalleryItems(block, ids), moved: moving.length };

    if (!Array.isArray(target.blocks)) {
        const gallery = target.gallery || { type: 'none', images: [] };
        next.target = {
            ...target,
            gallery: {
                ...gallery,
                images: [...(gallery.images || []), ...moving.map(legacyEntry)],
                crops: { ...(gallery.crops || {}), ...crops },
            },
        };
        return next;
    }

    const into = target.blocks.find((entry) => entry.type === BLOCK_TYPES.GALLERY && entry.layout === GALLERY_LAYOUTS.MASONRY);
    if (into) {
        next.target = {
            ...target,
            blocks: updateBlock(target.blocks, into.id, {
                items: [...(into.items || []), ...moving],
                crops: { ...(into.crops || {}), ...crops },
            }),
        };
    } else {
        next.target = {
            ...target,
            blocks: [...target.blocks, createBlock(BLOCK_TYPES.GALLERY, { items: moving, crops })],
        };
    }
    return next;
}

/** Insert uploaded images into a gallery block, before `beforeId` or at the end. */
export function insertGalleryImages(block, fileIds, beforeId) {
    if (!block || block.type !== BLOCK_TYPES.GALLERY) return block;
    const ids = (fileIds || []).filter(Boolean);
    if (!ids.length) return block;
    const items = [...(block.items || [])];
    let at = items.length;
    if (beforeId) {
        const idx = items.findIndex((item) => itemKey(item) === beforeId);
        if (idx >= 0) at = idx;
    }
    items.splice(at, 0, ...ids.map((fileId) => ({ type: ITEM_TYPES.IMAGE, fileId })));
    return { ...block, items };
}

/** Write HTML into one block, including a cell inside a columns block. */
export function patchBlockHtml(blocks, id, html) {
    let changed = false;
    const next = mapBlocks(blocks, (block) => {
        if (block.id !== id || (block.html || '') === html) return block;
        changed = true;
        return { ...block, html };
    });
    return changed ? next : blocks;
}

export default {
    STACK_GALLERY_TYPES,
    isStackPage,
    pageLayoutOf,
    stackDraft,
    setPageLayout,
    stackBodyFields,
    findBlock,
    updateBlock,
    removeBlock,
    moveBlock,
    reorderBlocks,
    insertBlock,
    itemKey,
    reorderSubset,
    editGalleryBlock,
    patchBlockHtml,
};
